<script setup lang="ts">
// Structured add/remove rows for the tool slots a task version declares, with a JSON toggle for power users. Every
// field is the kit's, as in TaskInputsEditor: it states no border, fill or radius of its own.
//
// A slot is a key the task's tools are named by (`codehost`, `codehost.create_issue`), a label a person choosing a
// connection reads, and an optional description. The key is checked with the shared rule when the slot is added and
// is never changed on a row after, since the tools name it: to rename one, remove it and add it again.

import {
    MAX_TOOL_SLOT_DESCRIPTION,
    MAX_TOOL_SLOT_LABEL,
    MAX_TOOL_SLOTS,
    type ToolSlot,
    toolSlotKeyProblem,
} from '@zerohuman/shared'
import { Button, Input, ZhPanel } from '@zerohuman/ui'
import { ref } from 'vue'
import RowsJsonSection from '@/components/RowsJsonSection.vue'
import { useRowsModel } from '@/composables/useRowsModel'

/** A row as the fields hold it: a description is always text here, and empty is stored as none. */
type SlotRow = { key: string; label: string; description: string }

const props = defineProps<{ modelValue: ToolSlot[] }>()
const emit = defineEmits<{ 'update:modelValue': [ToolSlot[]] }>()

const { rows, showJson, jsonText, jsonError, append, removeAt, toggleJson } = useRowsModel<SlotRow, ToolSlot>({
    modelValue: () => props.modelValue,
    emit: value => emit('update:modelValue', value),
    toRow: slot => ({ key: slot.key, label: slot.label, description: slot.description ?? '' }),
    // A slot with no description is stored without one, so a blank field and a removed one are the same shape.
    toValue: row => {
        const description = row.description.trim()
        return description ? { key: row.key, label: row.label, description } : { key: row.key, label: row.label }
    },
})

const draft = ref<SlotRow>(emptyDraft())

/** Why the last add was refused, shown beside the add row until a slot is added. */
const addError = ref<string | null>(null)

function emptyDraft(): SlotRow {
    return { key: '', label: '', description: '' }
}

/** Why a slot key cannot be added, in words, or null when it can. The server applies the same rules on save. */
function keyIssue(key: string): string | null {
    const problem = toolSlotKeyProblem(key)
    if (problem === 'tool_slot_key_invalid') {
        return 'A slot key starts with a lowercase letter, then up to 31 lowercase letters, digits, - or _.'
    }
    if (problem === 'tool_slot_key_reserved') {
        return `"${key}" is the OS's own and cannot be a slot.`
    }
    if (rows.value.some(row => row.key === key)) {
        return 'This task already has a slot with that key.'
    }
    return null
}

/** A row's label, checked as the server will: required, and at most the shared length. */
function labelIssue(label: string): string | null {
    if (!label.trim()) return 'Give the slot a label.'
    if (label.trim().length > MAX_TOOL_SLOT_LABEL) return `Keep the label to ${MAX_TOOL_SLOT_LABEL} characters.`
    return null
}

function descriptionIssue(description: string): string | null {
    return description.trim().length > MAX_TOOL_SLOT_DESCRIPTION
        ? `Keep the description to ${MAX_TOOL_SLOT_DESCRIPTION} characters.`
        : null
}

function add(): void {
    if (rows.value.length >= MAX_TOOL_SLOTS) {
        addError.value = `A task declares at most ${MAX_TOOL_SLOTS} slots.`
        return
    }
    const key = draft.value.key.trim()
    const issue = keyIssue(key) ?? labelIssue(draft.value.label) ?? descriptionIssue(draft.value.description)
    if (issue) {
        addError.value = issue
        return
    }
    append({ key, label: draft.value.label.trim(), description: draft.value.description })
    addError.value = null
    draft.value = emptyDraft()
}
</script>

<template>
	<RowsJsonSection
		testid="task-tool-slots"
		title="Tool slots"
		:show-json="showJson"
		:json-text="jsonText"
		:json-error="jsonError"
		:json-rows="5"
		@update:json-text="jsonText = $event"
		@toggle="toggleJson"
	>
		<template #description>
			Name this task's tools by a slot key instead of one vendor's tool type (for example
			<code>codehost.create_issue</code>). Each company fills the slot with one of its own connections on the Tools
			page, so the task works whichever tool they connected.
		</template>
		<p v-if="!rows.length" class="text-muted-foreground text-sm">No tool slots.</p>
		<ul class="space-y-2">
			<ZhPanel
				v-for="(row, index) in rows"
				:key="index"
				as="li"
				data-testid="task-tool-slot-row"
				class="flex flex-wrap items-start gap-2"
			>
				<div class="grid gap-1 text-xs">
					Key
					<code class="py-2 font-mono text-sm" data-testid="task-tool-slot-key">{{ row.key }}</code>
				</div>
				<label class="grid gap-1 text-xs">
					Label
					<Input v-model="row.label" data-testid="task-tool-slot-label" :maxlength="MAX_TOOL_SLOT_LABEL" />
					<span v-if="labelIssue(row.label)" role="alert" class="text-destructive">{{ labelIssue(row.label) }}</span>
				</label>
				<label class="grid min-w-48 flex-1 gap-1 text-xs">
					Description
					<Input v-model="row.description" :maxlength="MAX_TOOL_SLOT_DESCRIPTION" />
				</label>
				<Button
					type="button"
					size="sm"
					variant="destructive"
					class="self-end"
					data-testid="task-tool-slot-remove"
					@click="removeAt(index)"
				>
					Remove
				</Button>
			</ZhPanel>
		</ul>
		<div class="flex flex-wrap items-end gap-2">
			<label class="grid gap-1 text-xs">
				Key
				<Input v-model="draft.key" data-testid="task-tool-slot-add-key" placeholder="codehost" />
			</label>
			<label class="grid gap-1 text-xs">
				Label
				<Input v-model="draft.label" data-testid="task-tool-slot-add-label" placeholder="Code host" />
			</label>
			<label class="grid min-w-48 flex-1 gap-1 text-xs">
				Description
				<Input v-model="draft.description" data-testid="task-tool-slot-add-description" placeholder="Where the code lives" />
			</label>
			<Button type="button" size="sm" variant="outline" data-testid="task-tool-slot-add" @click="add">Add slot</Button>
		</div>
		<p v-if="addError" data-testid="task-tool-slot-add-error" role="alert" class="text-destructive text-xs">{{ addError }}</p>
	</RowsJsonSection>
</template>
